import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import { isDynamicSidebarCategory } from "@/lib/constants/filter-categories";
import { getPrettyName } from "@/lib/utils/prettyName";
import useFilterDictionary from "@/store/filter-dictionary/filter-dictionary.slice";
import useNewsFeedFilters from "@/store/news-feed/filters/newsFeed-filter.slice";
import type { KeyMessageSlice } from "@/store/news-feed/key-messages/key-message.slice";

/**
 * Gets the human-readable label for a static filter enum value using the dictionary store.
 * Falls back to prettified raw value if dictionary is not available.
 *
 * @param filterDictionaryState - Optional filter dictionary state. If provided, uses this instead of calling getState().
 *                                This allows the function to be used in reactive contexts (like derive).
 */
export const getEnumLabel = (
  category: ShareVoiceSidebarCategory,
  value: string,
  filterDictionaryState?: ReturnType<typeof useFilterDictionary.getState>,
): string => {
  const dictionaryState =
    filterDictionaryState ?? useFilterDictionary.getState();
  const { getDictionaryForCategory } = dictionaryState;

  const dictionaryItems = getDictionaryForCategory(category);

  if (!dictionaryItems) {
    return getPrettyName(value);
  }

  const dictionaryItem = dictionaryItems.find(
    (item) => item.categoryLabel === value,
  );

  if (dictionaryItem) {
    return dictionaryItem.label;
  }

  // Fallback to prettified label
  return getPrettyName(value);
};

/**
 * Gets the human-readable label for a dynamic filter item by ID.
 * Uses dynamic filters store for lookup.
 * Returns empty string if label cannot be resolved to avoid displaying IDs.
 *
 * @param newsFeedFiltersState - Optional news feed filters state. If provided, uses this instead of calling getState().
 *                                This allows the function to be used in reactive contexts (like derive).
 */
export const getIdLabel = (
  category: string,
  id: string,
  newsFeedFiltersState?: ReturnType<typeof useNewsFeedFilters.getState>,
): string => {
  const filtersState = newsFeedFiltersState ?? useNewsFeedFilters.getState();
  const { dynamicFilters } = filtersState;
  const categoryData = dynamicFilters.get(category);

  if (!categoryData) {
    // Return empty string instead of ID to avoid displaying raw IDs
    return "";
  }

  const item = categoryData.itemsMap?.get(id);

  if (item?.label && item.label !== id) {
    return item.label;
  }

  // No valid label - return empty string instead of ID to avoid displaying raw IDs
  return "";
};

/** Checks if a category is a dynamic filter category (fixed list, not store-derived). */
export const isDynamicCategory = (category: string): boolean =>
  isDynamicSidebarCategory(category);

/**
 * Gets selected static filters with their labels.
 * Returns a map where each category contains selected items with human-readable labels.
 * Does not return loading state - that should be handled at the selector level.
 *
 * @example
 * ```typescript
 * const selectedFilters = new Map([
 *   ['PROMINENCE', ['HEADLINE']],
 *   ['CATEGORY', ['NEWS']]
 * ]);
 *
 * const result = getSelectedStaticFiltersWithLabels(selectedFilters);
 *  Returns: Map {
 *    'PROMINENCE' => ['Headline'],
 *    'CATEGORY' => ['News Articles']
 *  }
 * ```
 */
/**
 * @param filterDictionaryState - Optional filter dictionary state. If provided, uses this instead of calling getState().
 */
export const getSelectedStaticFiltersWithLabels = (
  selectedFilters: Map<ShareVoiceSidebarCategory, string[]>,
  filterDictionaryState?: ReturnType<typeof useFilterDictionary.getState>,
): Map<ShareVoiceSidebarCategory, string[]> => {
  const result = new Map<ShareVoiceSidebarCategory, string[]>();

  for (const [category, selectedValues] of selectedFilters) {
    // Skip KEY_MESSAGE - it's handled separately by getSelectedKeyMessagesWithLabels
    if (category === "KEY_MESSAGE") {
      continue;
    }

    // Skip dynamic categories - only process static ones
    // Dynamic categories are handled by getSelectedDynamicFiltersWithLabels
    if (isDynamicCategory(category)) {
      continue;
    }

    const labels: string[] = [];

    // Convert each selected value to its label
    for (const value of selectedValues) {
      const label = getEnumLabel(category, value, filterDictionaryState);
      labels.push(label);
    }

    // Only add category if there are labels to show
    if (labels.length > 0) {
      result.set(category, labels);
    }
  }

  return result;
};

/**
 * Gets selected dynamic filters with their labels.
 * Returns a map where each category contains selected items with human-readable labels.
 * Only processes dynamic sidebar categories ({@link isDynamicCategory}).
 * Does not return loading state - that should be handled at the selector level.
 *
 * Important: Only returns labels for IDs that can be resolved. If an ID cannot be resolved
 * (e.g., data not loaded yet, or ID doesn't exist), it is skipped to avoid displaying raw IDs.
 *
 * @example
 * ```typescript
 * const selectedFilters = new Map([
 *   ['THEME', ['123', '456']],
 *   ['AUTHOR', ['789']]
 * ]);
 *
 * const result = getSelectedDynamicFiltersWithLabels(selectedFilters);
 *  Returns: Map {
 *    'THEME' => ['Technology', 'Sports'],
 *    'AUTHOR' => ['John Smith']
 *  }
 * ```
 */
/**
 * @param newsFeedFiltersState - Optional news feed filters state. If provided, uses this instead of calling getState().
 */
export const getSelectedDynamicFiltersWithLabels = (
  selectedFilters: Map<string, string[]>,
  newsFeedFiltersState?: ReturnType<typeof useNewsFeedFilters.getState>,
): Map<string, string[]> => {
  const result = new Map<string, string[]>();
  const filtersState = newsFeedFiltersState ?? useNewsFeedFilters.getState();
  const { dynamicFilters } = filtersState;

  for (const [category, selectedIds] of selectedFilters) {
    // Skip KEY_MESSAGE - it's handled separately by getSelectedKeyMessagesWithLabels
    if (category === "KEY_MESSAGE") {
      continue;
    }

    // Skip static categories - only process dynamic ones
    if (!isDynamicCategory(category)) {
      continue;
    }

    const categoryData = dynamicFilters.get(category);
    if (!categoryData) {
      // Don't return anything if category data doesn't exist yet
      continue;
    }

    const itemsMap = categoryData.itemsMap;
    if (!itemsMap || itemsMap.size === 0) {
      // If no itemsMap or it's empty, skip entirely to avoid showing IDs
      // The loading state will be handled by the selector
      continue;
    }

    // Convert each selected ID to its label
    // Dynamic filters store IDs, so we look up the label for each ID
    // Use a Set to deduplicate labels and track which labels we've already added
    const labels: string[] = [];
    const seenLabels = new Set<string>();

    for (const id of selectedIds) {
      const label = getIdLabel(category, id, newsFeedFiltersState);

      // Only add if:
      // 1. Label is not empty (IdLabel returns empty string for invalid IDs)
      // 2. We haven't seen this label before (deduplication)
      if (label && !seenLabels.has(label)) {
        labels.push(label);
        seenLabels.add(label);
      }
    }

    // Only add category if there are valid labels to show
    // Empty arrays are not added, which means no filter pill will appear until labels are available
    if (labels.length > 0) {
      result.set(category, labels);
    }
  }

  return result;
};

/**
 * Dynamic categories selected in the URL whose pill labels are not resolved yet.
 * @param prefetchEnabled - When true (fetch-dynamic-filters-exp), show loading until fetch settles or errors.
 */
export const getLoadingDynamicFilterCategories = (
  selectedFilters: Map<string, string[]>,
  resolvedLabels: Map<string, string[]>,
  newsFeedFiltersState?: ReturnType<typeof useNewsFeedFilters.getState>,
  prefetchEnabled = false,
): ShareVoiceSidebarCategory[] => {
  const loading: ShareVoiceSidebarCategory[] = [];
  const filtersState = newsFeedFiltersState ?? useNewsFeedFilters.getState();

  for (const [category, selectedIds] of selectedFilters) {
    if (!isDynamicCategory(category) || selectedIds.length === 0) {
      continue;
    }
    if (resolvedLabels.has(category)) {
      continue;
    }

    const categoryData = filtersState.dynamicFilters.get(category);

    if (categoryData && !categoryData.isLoading) {
      continue;
    }

    if (categoryData?.isLoading || prefetchEnabled) {
      loading.push(category as ShareVoiceSidebarCategory);
    }
  }

  return loading;
};

/**
 * Gets selected key messages with their labels.
 * Returns a map where "KEY_MESSAGE" contains selected items with human-readable labels.
 * Does not return loading state - that should be handled at the selector level.
 *
 * @example
 * ```typescript
 * const selectedIds = ['msg1', 'msg2'];
 * const result = getSelectedKeyMessagesWithLabels(selectedIds);
 *  Returns: Map {
 *    'KEY_MESSAGE' => ['Brand Awareness Campaign', 'Product Launch']
 *  }
 * ```
 */
/**
 * @param keyMessageState - Optional key message state. If provided, uses this instead of calling getState().
 *                          This allows the function to be used in reactive contexts (like derive).
 *                          Only needs to have keyMessages property.
 */
export const getSelectedKeyMessagesWithLabels = (
  selectedIds: string[],
  keyMessageState?: {
    keyMessages: readonly KeyMessageSlice["keyMessages"][number][];
  },
): Map<"KEY_MESSAGE", string[]> => {
  const result = new Map<"KEY_MESSAGE", string[]>();
  const labels: string[] = [];

  if (!keyMessageState) {
    // If no state provided, return empty map
    return result;
  }

  const { keyMessages } = keyMessageState;

  // Convert each selected ID to its label
  for (const id of selectedIds) {
    const keyMessage = keyMessages.find((km) => km.id === id);
    if (keyMessage) {
      labels.push(keyMessage.name);
    } else {
      // Fallback to a default label if key message not found
      labels.push(`Key Topic ${id}`);
    }
  }

  // Only add category if there are labels to show
  if (labels.length > 0) {
    result.set("KEY_MESSAGE", labels);
  }

  return result;
};
